/**
 * spanishVoices.js — Cuál de las voces instaladas lee mejor el español de acá.
 *
 * El problema que resuelve es concreto y se escucha a la primera: dejada a su
 * criterio, la plataforma elige la voz POR DEFECTO DEL SISTEMA, que en la
 * mayoría de los equipos es inglesa. Una voz inglesa leyendo la frase de una
 * alerta convierte "Jesús" en "yísus", "Valledupar" en algo irreconocible y la
 * placa en ruido — justo el dato que la locución existe para transmitir.
 *
 * Y no alcanza con pedir `lang: "es-CO"`: eso es una preferencia, no una
 * orden. Los motores caen a su voz por defecto cuando no tienen exactamente esa
 * región, y varios ignoran el `lang` si ya hay una voz seleccionada. La única
 * forma de garantizar acento en español es ELEGIR la voz a mano.
 *
 * Este módulo es la fuente única de ese criterio: lo usan la Web Speech API
 * (`lib/speech.js`), el selector de la tarjeta de ajustes y —con la misma tabla
 * transcrita— el motor nativo de Android (`SpanishVoices.java`).
 */

/**
 * Regiones en orden de preferencia para una flota colombiana.
 *
 * `es-US` está segundo y no es un error: la voz "Google español de Estados
 * Unidos" es el latino NEUTRO que Chrome y Android traen instalado, y es la que
 * de verdad va a existir en el equipo. `es-419` es la etiqueta oficial de
 * "español de Latinoamérica" y casi nadie la publica, pero cuando aparece es
 * exactamente lo que se busca.
 *
 * El peninsular (`es-ES`) va ÚLTIMO, después incluso del `es` sin región: se
 * entiende perfecto, pero el seseo y el vocabulario no son los de acá y suena a
 * locución ajena. Sigue siendo infinitamente mejor que una voz inglesa, así que
 * está en la lista y no fuera de ella.
 */
const REGION_RANK = [
  "es-co",
  "es-us",
  "es-419",
  "es-mx",
  "es-ar", "es-cl", "es-pe", "es-ve", "es-ec", "es-bo", "es-py", "es-uy",
  "es-cr", "es-cu", "es-do", "es-gt", "es-hn", "es-ni", "es-pa", "es-pr", "es-sv",
  "es",
  "es-es",
];

/** Peso de una región desconocida pero española (`es-gq`, y lo que venga). */
const UNKNOWN_REGION_RANK = REGION_RANK.indexOf("es");

/** ¿Esta etiqueta de idioma es español? Acepta `es`, `es-CO` y `es_CO`. */
export function isSpanishLang(lang) {
  return /^es([-_]|$)/i.test(String(lang ?? "").trim());
}

function normalizeLang(lang) {
  return String(lang ?? "").trim().toLowerCase().replace("_", "-");
}

/**
 * Desempate por calidad del motor, a igualdad de región.
 *
 * Las voces neuronales ("Natural", "Neural") son de otra generación: entonan
 * las frases en vez de encadenar sílabas, y en una alerta eso es la diferencia
 * entre entender la placa a la primera o tener que escucharla de nuevo. Las de
 * Google vienen después porque son las que están instaladas en todos lados y su
 * pronunciación del español es correcta, aunque plana.
 */
function qualityRank(name) {
  const n = String(name ?? "").toLowerCase();
  if (/natural|neural/.test(n)) return 0;
  if (/google/.test(n)) return 1;
  return 2;
}

/**
 * Puntaje de una voz: **menor es mejor**. `Infinity` = no sirve.
 *
 * Una voz que no es española nunca gana, por más que sea la del sistema: es
 * justamente el caso que este módulo existe para evitar.
 *
 * @param {{lang?: string, name?: string}} voice
 * @returns {number}
 */
export function scoreVoice(voice) {
  const lang = normalizeLang(voice?.lang);
  if (!isSpanishLang(lang)) return Infinity;

  const region = REGION_RANK.indexOf(lang);
  const rank = region === -1 ? UNKNOWN_REGION_RANK : region;
  // La región pesa siempre más que la calidad: una voz neuronal peninsular
  // sigue sonando ajena, y una voz plana colombiana no.
  return rank * 10 + qualityRank(voice?.name);
}

/**
 * La voz recomendada entre las instaladas, o `null` si no hay ninguna en
 * español.
 *
 * Es la MISMA respuesta que `sortSpanishVoices(voices)[0]` —hay un test que lo
 * exige— y por eso `speech.js` expone una sola: `recommendedVoice()` devuelve la
 * primera de la lista ordenada. Esta función queda como la definición legible
 * del criterio y como el otro lado de esa igualdad en el test; no se llama
 * desde producción a propósito, para que no haya dos cuentas que puedan
 * discrepar.
 *
 * @param {Array<{lang?: string, name?: string}>} voices
 */
export function pickSpanishVoice(voices) {
  let mejor = null;
  let mejorPuntaje = Infinity;
  for (const voice of voices ?? []) {
    const puntaje = scoreVoice(voice);
    // Estrictamente menor: ante un empate gana la que el sistema listó primero,
    // que es la que el usuario ve como "la de siempre" en el resto del equipo.
    if (puntaje < mejorPuntaje) {
      mejor = voice;
      mejorPuntaje = puntaje;
    }
  }
  return mejor;
}

/**
 * Las voces en español, de la más recomendable a la menos.
 *
 * Es el orden del selector de la tarjeta: la primera de la lista es la misma
 * que se usa cuando el usuario no elige ninguna, así que "Recomendada" y lo que
 * sale arriba no pueden discrepar.
 *
 * @param {Array<{lang?: string, name?: string}>} voices
 */
export function sortSpanishVoices(voices) {
  return (voices ?? [])
    .filter((v) => isSpanishLang(v?.lang))
    .map((v, i) => ({ v, i, score: scoreVoice(v) }))
    // El índice original desempata para que el orden sea estable entre
    // renders: `Array.prototype.sort` no lo garantiza en todos los motores.
    .sort((a, b) => a.score - b.score || a.i - b.i)
    .map(({ v }) => v);
}
